import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { calcularPlan } from './calcularPlan';

const PRECIO_EJEMPLO = 1000000;
const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

// FIX: antes este componente no recibía miRol/miAgente en absoluto —
// cualquiera que llegara a esta pantalla (por ejemplo un Agente, si
// alguna vez se le mostrara el botón "Planes") podía editar/eliminar
// planes de pago sin ningún candado. Ahora solo puede editar/agregar/
// eliminar Super Admin, Admin, o Gerente Externo cuando el desarrollo
// está entre sus desarrollos_cargo.
// Nombre de plan: sin espacios de más y con la primera letra en mayúscula (igual que la base de datos).
const normalizarNombrePlan = (t) => {
  const limpio = (t || '').replace(/\s+/g, ' ').trim();
  return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1).toLowerCase() : '';
};
// Texto de promoción: sin espacios sobrantes y empezando con mayúscula (conserva el resto como se escribió).
const normalizarTextoPromo = (t) => {
  const limpio = (t || '').split('\n').map(l => l.replace(/[ \t]+/g, ' ').trim()).join('\n').trim();
  return limpio ? limpio.charAt(0).toUpperCase() + limpio.slice(1) : '';
};

export default function Planes({ desarrollo, onBack, miRol, miAgente }) {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  // FIX: selector de torre/etapa, igual que en Inventario
  const [estructuraSel, setEstructuraSel] = useState(desarrollo.tiene_etapas ? `${desarrollo.tipo_estructura} 1` : null);

  // Promociones del desarrollo (o de la torre/etapa seleccionada): cada una con texto + vigencia.
  // Un item sin id todavía no se ha guardado.
  const [promos, setPromos] = useState([]);
  const [tipologiasDisp, setTipologiasDisp] = useState([]);
  const [numerosDisp, setNumerosDisp] = useState([]);
  const [usadas, setUsadas] = useState({});
  const [guardandoPromo, setGuardandoPromo] = useState(null);
  const [msgPromo, setMsgPromo] = useState({});

  const puedeEditar = miRol === 'Super Admin' || miRol === 'Admin' ||
    (miRol === 'Gerente Externo' && (miAgente?.desarrollos_cargo || []).includes(desarrollo.nombre));

  useEffect(() => {
    // Si no tiene etapas, carga directo. Si tiene etapas, espera a que
    // estructuraSel tenga valor (ya viene con default arriba).
    if (!desarrollo.tiene_etapas || estructuraSel) cargarPlanes();
  }, [estructuraSel]);

  const estructuraPromo = desarrollo.tiene_etapas ? (estructuraSel || '') : '';

  useEffect(() => {
    if (desarrollo.tiene_etapas && !estructuraSel) return;
    cargarPromos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estructuraSel]);

  const cargarPromos = async () => {
    const { data } = await supabase.from('promociones_desarrollo').select('*')
      .eq('desarrollo_id', desarrollo.id).eq('estructura', estructuraPromo).order('created_at');
    setPromos((data || []).map(p => ({
      id: p.id, texto: p.texto || '', vigencia_desde: p.vigencia_desde || '', vigencia_hasta: p.vigencia_hasta || '', activa: p.activa !== false,
      tipologias: p.tipologias || [], unidadesTexto: (p.unidades || []).join(', '), tope: p.tope_unidades == null ? '' : String(p.tope_unidades),
    })));
    setMsgPromo({});
    const ids = (data || []).map(p => p.id);
    if (ids.length > 0) {
      const { data: uso } = await supabase.rpc('promociones_usadas', { ids });
      setUsadas(Object.fromEntries((uso || []).map(u => [u.promocion_id, Number(u.usadas)])));
    } else setUsadas({});
    // Prototipos (tipologías) que existen en este desarrollo / torre, para poder limitar la promoción.
    let qInv = supabase.from('inventario').select('tipologia, numero').eq('desarrollo_id', desarrollo.id);
    if (desarrollo.tiene_etapas) qInv = qInv.eq('estructura', estructuraPromo);
    const { data: inv } = await qInv.limit(5000);
    setTipologiasDisp([...new Set((inv || []).map(u => (u.tipologia || '').trim()).filter(Boolean))].sort());
    setNumerosDisp((inv || []).map(u => String(u.numero || '').trim()).filter(Boolean));
  };

  const handleAgregarPromo = () => {
    if (!puedeEditar) return;
    setPromos(prev => [...prev, { id: null, clave: `nueva-${Date.now()}`, texto: '', vigencia_desde: '', vigencia_hasta: '', activa: true, tipologias: [], unidadesTexto: '', tope: '' }]);
  };

  const cambiarPromo = (idx, campo, valor) => setPromos(prev => prev.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));

  const handleGuardarPromo = async (idx) => {
    if (!puedeEditar) return;
    const p = promos[idx];
    const llave = p.id || p.clave;
    const textoLimpio = normalizarTextoPromo(p.texto);
    if (!textoLimpio) { setMsgPromo(m => ({ ...m, [llave]: '❌ Escribe el texto de la promoción.' })); return; }
    if (p.vigencia_desde && p.vigencia_hasta && p.vigencia_desde > p.vigencia_hasta) {
      setMsgPromo(m => ({ ...m, [llave]: '❌ La fecha de inicio no puede ser posterior a la de fin.' })); return;
    }
    const unidades = [...new Set((p.unidadesTexto || '').split(/[\s,;]+/).map(x => x.trim()).filter(Boolean))];
    const topeNum = p.tope === '' || p.tope == null ? null : parseInt(p.tope, 10);
    if (topeNum !== null && (!Number.isFinite(topeNum) || topeNum < 1)) {
      setMsgPromo(m => ({ ...m, [llave]: '❌ El tope de unidades debe ser un número mayor a 0.' })); return;
    }
    setGuardandoPromo(llave);
    const datos = {
      texto: textoLimpio, vigencia_desde: p.vigencia_desde || null, vigencia_hasta: p.vigencia_hasta || null,
      activa: p.activa, tipologias: p.tipologias || [], unidades, tope_unidades: topeNum, updated_at: new Date().toISOString(),
    };
    let error, nuevo;
    if (p.id) {
      ({ error } = await supabase.from('promociones_desarrollo').update(datos).eq('id', p.id));
    } else {
      ({ data: nuevo, error } = await supabase.from('promociones_desarrollo')
        .insert([{ ...datos, desarrollo_id: desarrollo.id, estructura: estructuraPromo }]).select().single());
    }
    setGuardandoPromo(null);
    if (error) { setMsgPromo(m => ({ ...m, [llave]: '❌ No se pudo guardar: ' + error.message })); return; }
    setPromos(prev => prev.map((x, i) => i === idx ? { ...x, texto: textoLimpio } : x));
    if (nuevo) setPromos(prev => prev.map((x, i) => i === idx ? { ...x, id: nuevo.id, clave: undefined } : x));
    setMsgPromo(m => ({ ...m, [llave]: '✅ Guardada', ...(nuevo ? { [nuevo.id]: '✅ Guardada' } : {}) }));
  };

  const handleQuitarPromo = async (idx) => {
    if (!puedeEditar) return;
    const p = promos[idx];
    if (!p.id) { setPromos(prev => prev.filter((_, i) => i !== idx)); return; }
    if (!window.confirm('¿Quitar esta promoción?')) return;
    const { error } = await supabase.from('promociones_desarrollo').delete().eq('id', p.id);
    if (error) { setMsgPromo(m => ({ ...m, [p.id]: '❌ No se pudo quitar: ' + error.message })); return; }
    setPromos(prev => prev.filter((_, i) => i !== idx));
  };

  const hoyIso = new Date().toLocaleDateString('en-CA');
  const estadoPromo = (p) => !p.id ? 'Sin guardar'
    : !p.activa ? 'Desactivada'
    : p.vigencia_hasta && p.vigencia_hasta < hoyIso ? 'Vencida'
    : p.vigencia_desde && p.vigencia_desde > hoyIso ? 'Aún no inicia'
    : 'Vigente';

  const cargarPlanes = async () => {
    setLoading(true);
    let query = supabase.from('planes_pago').select('*').eq('desarrollo_id', desarrollo.id);
    // FIX: si el desarrollo tiene torres/etapas, solo trae los planes de la torre seleccionada
    if (desarrollo.tiene_etapas) query = query.eq('estructura', estructuraSel);
    const { data } = await query.order('created_at');
    setPlanes(data || []);
    setLoading(false);
  };

  const handleAgregar = async () => {
    if (!puedeEditar) return; // FIX: respaldo de seguridad
    const nuevo = {
      desarrollo_id: desarrollo.id,
      nombre: `Plan ${planes.length + 1}`,
      activo: false,
      descuento_preventa: 0,
      descuento_plan: 0,
      enganche: 0,
      durante_obra: 0,
      meses_plan: 0,
      // FIX: el plan queda asociado a la torre/etapa seleccionada (o null si el desarrollo no tiene etapas)
      estructura: desarrollo.tiene_etapas ? estructuraSel : null
    };
    await supabase.from('planes_pago').insert([nuevo]);
    cargarPlanes();
  };

  const handleEliminar = async (id) => {
    if (!puedeEditar) return; // FIX: respaldo de seguridad
    if (!window.confirm('¿Eliminar este plan?')) return;
    await supabase.from('planes_pago').delete().eq('id', id);
    cargarPlanes();
  };

  const handleCambio = (id, campo, valor) => {
    if (!puedeEditar) return; // FIX: respaldo de seguridad
    setPlanes(prev => prev.map(p => p.id === id ? { ...p, [campo]: valor } : p));
  };

  const handleGuardar = async (plan) => {
    if (!puedeEditar) return; // FIX: respaldo de seguridad
    setGuardando(true);
    const nombreLimpio = normalizarNombrePlan(plan.nombre);
    setPlanes(prev => prev.map(x => x.id === plan.id ? { ...x, nombre: nombreLimpio } : x));
    await supabase.from('planes_pago').update({
      nombre: nombreLimpio,
      activo: plan.activo,
      descuento_preventa: plan.descuento_preventa,
      descuento_plan: plan.descuento_plan,
      enganche: plan.enganche,
      durante_obra: plan.durante_obra,
      meses_plan: plan.meses_plan
    }).eq('id', plan.id);
    setGuardando(false);
  };

  const inp = (plan, label, campo, type = 'number') => (
    <div style={{ flex: 1 }}>
      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>{label}</label>
      <input type={type} value={plan[campo]} onChange={e => handleCambio(plan.id, campo, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        onBlur={campo === 'nombre' && puedeEditar ? () => handleCambio(plan.id, 'nombre', normalizarNombrePlan(plan[campo])) : undefined}
        readOnly={!puedeEditar}
        style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: puedeEditar ? '#fff' : '#f9f9f9', color: puedeEditar ? '#000' : '#888' }} />
    </div>
  );

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#4A4A4A' }}>
          ← Volver a Desarrollos
        </button>
        {puedeEditar && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleAgregar} style={btnOutline}>+ Agregar nuevo plan</button>
          </div>
        )}
      </div>

      <h2 style={{ fontSize: '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>{desarrollo.nombre}</h2>
      <div style={{ fontSize: '13px', color: '#888', marginBottom: '1rem' }}>
        Planes de pago{estructuraSel ? ` — ${estructuraSel}` : ''}
      </div>

      {/* FIX: selector de torre/etapa — misma lógica que Inventario.js */}
      {desarrollo.tiene_etapas && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {Array.from({ length: desarrollo.num_estructuras || 1 }, (_, i) => {
            const nombre = `${desarrollo.tipo_estructura} ${i + 1}`;
            return (
              <button key={i} onClick={() => setEstructuraSel(nombre)}
                style={{ padding: '6px 16px', borderRadius: '20px', border: '0.5px solid', cursor: 'pointer', fontSize: '13px',
                  background: estructuraSel === nombre ? '#4A4A4A' : '#fff',
                  color: estructuraSel === nombre ? '#fff' : '#666',
                  borderColor: estructuraSel === nombre ? '#4A4A4A' : '#ddd' }}>
                {nombre}
              </button>
            );
          })}
        </div>
      )}

      {/* Promociones: aparecen en la cotización, debajo de los detalles de la unidad, mientras estén vigentes */}
      <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
          <span style={{ fontSize: '15px', fontWeight: '500', color: '#1a1a2e' }}>Promociones{estructuraSel ? ` — ${estructuraSel}` : ''}</span>
          {puedeEditar && <button onClick={handleAgregarPromo} style={btnOutline}>+ Agregar promoción</button>}
        </div>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: promos.length ? '12px' : 0 }}>
          Se muestran en la cotización, debajo de "Detalles de la unidad", solo dentro de su vigencia. Puedes tener varias a la vez.
        </div>
        {promos.length === 0 && <div style={{ fontSize: '13px', color: '#aaa', marginTop: '8px' }}>Sin promociones{puedeEditar ? '. Usa "+ Agregar promoción".' : '.'}</div>}
        {promos.map((promo, idx) => {
          const llave = promo.id || promo.clave;
          const estado = estadoPromo(promo);
          return (
            <div key={llave} style={{ border: '0.5px solid #e8e8e8', borderRadius: '10px', padding: '12px 14px', marginBottom: '10px', background: '#fcfcfc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '500', color: '#555' }}>Promoción {idx + 1}</span>
                <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', fontWeight: '500',
                  background: estado === 'Vigente' ? '#EAF3DE' : '#f0f0f0', color: estado === 'Vigente' ? '#27500A' : '#888' }}>{estado}</span>
              </div>
              <textarea value={promo.texto} onChange={e => cambiarPromo(idx, 'texto', e.target.value)} readOnly={!puedeEditar}
                placeholder='Ej. 5% de descuento adicional pagando de contado antes del 30 de noviembre' rows={2}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', background: puedeEditar ? '#fff' : '#f9f9f9' }} />
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '8px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Vigente desde (opcional)</label>
                  <input type='date' value={promo.vigencia_desde} onChange={e => cambiarPromo(idx, 'vigencia_desde', e.target.value)} disabled={!puedeEditar}
                    style={{ padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Vigente hasta (opcional)</label>
                  <input type='date' value={promo.vigencia_hasta} onChange={e => cambiarPromo(idx, 'vigencia_hasta', e.target.value)} disabled={!puedeEditar}
                    style={{ padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#666', paddingBottom: '8px' }}>
                  <input type='checkbox' checked={promo.activa} disabled={!puedeEditar} onChange={e => cambiarPromo(idx, 'activa', e.target.checked)} />
                  Activa
                </label>
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
                  Aplica a: {promo.tipologias.length === 0 ? 'todos los prototipos' : `prototipo${promo.tipologias.length > 1 ? 's' : ''} ${promo.tipologias.join(', ')}`}
                </div>
                {tipologiasDisp.length > 0 ? (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {tipologiasDisp.map(t => {
                      const sel = promo.tipologias.includes(t);
                      return (
                        <button key={t} disabled={!puedeEditar}
                          onClick={() => cambiarPromo(idx, 'tipologias', sel ? promo.tipologias.filter(x => x !== t) : [...promo.tipologias, t])}
                          style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', cursor: puedeEditar ? 'pointer' : 'default',
                            border: `0.5px solid ${sel ? '#4A4A4A' : '#ddd'}`, background: sel ? '#4A4A4A' : '#fff', color: sel ? '#fff' : '#666' }}>{t}</button>
                      );
                    })}
                    {puedeEditar && promo.tipologias.length > 0 && (
                      <button onClick={() => cambiarPromo(idx, 'tipologias', [])} style={{ background: 'none', border: 'none', color: '#888', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer' }}>Todos</button>
                    )}
                  </div>
                ) : <div style={{ fontSize: '12px', color: '#bbb' }}>Este desarrollo no tiene prototipos capturados en el inventario.</div>}
              </div>
              {(() => {
                const lista = [...new Set((promo.unidadesTexto || '').split(/[\s,;]+/).map(x => x.trim()).filter(Boolean))];
                const noExisten = numerosDisp.length > 0 ? lista.filter(n => !numerosDisp.includes(n)) : [];
                const usadasPromo = promo.id ? (usadas[promo.id] || 0) : 0;
                return (
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                    <div style={{ flex: '1 1 260px' }}>
                      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Solo para estas unidades (opcional)</label>
                      <input value={promo.unidadesTexto} onChange={e => cambiarPromo(idx, 'unidadesTexto', e.target.value)} readOnly={!puedeEditar}
                        placeholder='Ej. 301, 302, 405 — vacío = todas las unidades'
                        style={{ width: '100%', padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: puedeEditar ? '#fff' : '#f9f9f9' }} />
                      <div style={{ fontSize: '11px', color: noExisten.length ? '#e53935' : '#aaa', marginTop: '3px' }}>
                        {lista.length === 0 ? 'Aplica a todas las unidades (según los prototipos elegidos).'
                          : `${lista.length} unidad${lista.length > 1 ? 'es' : ''}${noExisten.length ? ` — no encuentro en el inventario: ${noExisten.join(', ')}` : ''}`}
                      </div>
                    </div>
                    <div style={{ width: '190px' }}>
                      <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>Tope de unidades (opcional)</label>
                      <input type='number' min='1' value={promo.tope} onChange={e => cambiarPromo(idx, 'tope', e.target.value)} readOnly={!puedeEditar}
                        placeholder='Hasta agotar existencias'
                        style={{ width: '100%', padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: puedeEditar ? '#fff' : '#f9f9f9' }} />
                      <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>
                        {promo.tope ? `${usadasPromo} de ${promo.tope} unidades usadas` : 'Sin tope'}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {puedeEditar && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                  <button onClick={() => handleGuardarPromo(idx)} disabled={guardandoPromo === llave} style={{ ...btnPrimary, padding: '7px 20px' }}>
                    {guardandoPromo === llave ? 'Guardando...' : 'Guardar'}
                  </button>
                  <button onClick={() => handleQuitarPromo(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: '13px' }}>Quitar</button>
                  {msgPromo[llave] && <span style={{ fontSize: '12px', color: '#666' }}>{msgPromo[llave]}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888' }}>Cargando...</div>
      ) : planes.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
          Sin planes de pago{estructuraSel ? ` para ${estructuraSel}` : ''}. {puedeEditar ? 'Agrega uno.' : ''}
        </div>
      ) : (
        planes.map((plan, i) => (
          <div key={plan.id} style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '15px', fontWeight: '500', color: '#1a1a2e' }}>Plan de pagos {i + 1}</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#666', cursor: puedeEditar ? 'pointer' : 'default' }}>
                  <input type='checkbox' checked={plan.activo} disabled={!puedeEditar} onChange={e => handleCambio(plan.id, 'activo', e.target.checked)} />
                  Activar
                </label>
              </div>
              {puedeEditar && (
                <button onClick={() => handleEliminar(plan.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: '13px' }}>
                  Eliminar plan
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
              {inp(plan, 'Nombre', 'nombre', 'text')}
              {inp(plan, '% Descuento Preventa', 'descuento_preventa')}
              {inp(plan, '% Descuento Plan', 'descuento_plan')}
              {inp(plan, '% Enganche', 'enganche')}
              {inp(plan, '% Durante la obra', 'durante_obra')}
              {inp(plan, `Meses plan ${i + 1}`, 'meses_plan')}
            </div>

            {/* FIX: la vista previa usa la misma fórmula que el cotizador
                (calcularPlan): incluye el descuento de preventa, las
                mensualidades reparten solo el % durante la obra y el resto
                se muestra como pago a la entrega. */}
            {(() => {
              const calc = calcularPlan(plan, PRECIO_EJEMPLO);
              return (
                <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                  <div style={{ fontWeight: '500', marginBottom: '4px', color: '#333' }}>Vista previa (sobre precio de ejemplo {fmt(PRECIO_EJEMPLO)})</div>
                  <div>Precio con descuentos: {fmt(calc.precioConDescuento)}</div>
                  <div>Enganche ({plan.enganche}%): {fmt(calc.enganche)}</div>
                  <div>Durante la obra ({plan.durante_obra}%): {fmt(calc.duranteObra)}</div>
                  {calc.mesesUsados > 0 && <div>{calc.mesesUsados} mensualidades de: {fmt(calc.mensualidad)}</div>}
                  <div>A la entrega ({calc.restoPorc}%): {fmt(calc.entrega)}</div>
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>En el cotizador, el número de mensualidades se ajusta a los meses que faltan para la entrega de cada unidad.</div>
                </div>
              );
            })()}

            {puedeEditar && (
              <button onClick={() => handleGuardar(plan)} disabled={guardando} style={{ ...btnPrimary, padding: '8px 24px' }}>
                {guardando ? 'Guardando...' : 'Guardar plan'}
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}

const btnPrimary = { background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' };
const btnOutline = { background: '#fff', color: '#333', border: '0.5px solid #ddd', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' };